import {
  type ReactNode,
  type ThHTMLAttributes,
  type TdHTMLAttributes,
} from "react";
import { NUMERIC } from "./tokens";

// 어드민 표 — 헤더는 배경색 대신 하단 1px 로만 구분하고, 행 구분선도 최소한으로 둔다.
// 좁은 화면에서는 래퍼가 가로 스크롤한다 (셀 값은 모두 한 줄 단위 정보라 줄바꿈하지 않는다).
export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  );
}

// groups: 개념이 다른 열 묶음(예: 계약 / 정산)을 열 제목 위 한 줄로 묶어 보여준다.
// 묶음을 쓰면 각 묶음의 첫 열에 TGroup·TH·TD 가 함께 왼쪽 경계선을 그어야 세로로 이어진다.
export function THead({
  groups,
  children,
}: {
  groups?: ReactNode;
  children: ReactNode;
}) {
  return (
    <thead>
      {groups && <tr className="whitespace-nowrap text-left">{groups}</tr>}
      <tr className="whitespace-nowrap border-b border-slate-200 text-left">
        {children}
      </tr>
    </thead>
  );
}

// 열 묶음 제목 — 이름 없는 구간은 children 없이 자리만 차지한다.
export function TGroup({
  span,
  children,
}: {
  span: number;
  children?: ReactNode;
}) {
  return (
    <th
      colSpan={span}
      className={`px-3 pb-1 pt-2.5 text-xs font-semibold text-slate-400 ${
        children ? "border-l border-slate-200 text-center" : ""
      }`}
    >
      {children}
    </th>
  );
}

// help 를 주면 제목에 점선 밑줄이 붙고 hover 시 네이티브 툴팁으로 설명이 뜬다.
// 아이콘을 따로 두지 않는다 — 열이 많은 표에서 아이콘은 숫자보다 시선을 끈다.
export function TH({
  numeric = false,
  className = "",
  help,
  children,
  ...rest
}: {
  numeric?: boolean;
  help?: string;
} & ThHTMLAttributes<HTMLTableCellElement>) {
  // 열 제목은 한글이라 uppercase/tracking 을 쓰지 않는다. 크기·무게로만 본문과 구분한다.
  return (
    <th
      className={`px-3 py-2.5 text-xs font-semibold text-slate-500 ${
        numeric ? "text-right" : ""
      } ${className}`}
      {...rest}
    >
      {help ? (
        <span
          title={help}
          className="cursor-help border-b border-dotted border-slate-300"
        >
          {children}
        </span>
      ) : (
        children
      )}
    </th>
  );
}

// className: 행 단위 강조용 (처리 대기 행 배경 등).
// 배경을 넘기면 hover 중에는 hover:bg-* 가 이긴다 (:hover 가 특이도 한 단계 위라 클래스 순서와 무관).
export function TRow({
  className = "",
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <tr
      className={`whitespace-nowrap border-b border-slate-100 transition-colors last:border-b-0 ${className} hover:bg-slate-50/70`}
    >
      {children}
    </tr>
  );
}

// muted: 부차 정보(기간·유형 등) — 본문 값보다 한 단계 약하게 둔다
// numeric: 우측 정렬 + tabular-nums 로 자리수를 맞춘다
export function TD({
  numeric = false,
  muted = false,
  className = "",
  children,
  ...rest
}: {
  numeric?: boolean;
  muted?: boolean;
} & TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td
      className={`px-3 py-2.5 ${muted ? "text-slate-500" : "text-slate-900"} ${
        numeric ? `text-right ${NUMERIC}` : ""
      } ${className}`}
      {...rest}
    >
      {children}
    </td>
  );
}

// 표가 비었을 때의 자리 — 왜 비었는지(데이터 없음 vs 필터 결과 없음)를 호출처가 정한다
export function TEmpty({
  colSpan,
  children,
}: {
  colSpan: number;
  children: ReactNode;
}) {
  return (
    <tr>
      <td
        className="px-3 py-12 text-center text-sm text-slate-400"
        colSpan={colSpan}
      >
        {children}
      </td>
    </tr>
  );
}

// 로딩 중 표 본문 — 스피너 대신 최종 레이아웃과 같은 모양의 스켈레톤 행을 보여준다
export function TSkeleton({
  colSpan,
  rows = 5,
}: {
  colSpan: number;
  rows?: number;
}) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="border-b border-slate-100 last:border-b-0">
          <td className="px-3 py-2.5" colSpan={colSpan}>
            <span className="block h-4 w-full animate-pulse rounded bg-slate-100" />
          </td>
        </tr>
      ))}
    </>
  );
}
